import { useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Pencil,
  UserX,
  FileText,
  Download,
  Trash2,
  Upload,
  Plus,
  Star,
  CheckCircle2,
  Circle,
  Eye,
  KeyRound,
  Trophy,
  AlertTriangle,
  Copy,
  Check,
  X,
} from 'lucide-react';
import {
  useEmployee,
  useEmployeeDocuments,
  useInitDocumentUpload,
  useDeleteDocument,
  useDocumentDownloadUrl,
  useConfirmDocument,
  useUpdateDocument,
  useDeleteEmployee,
  useEvaluations,
  useCreateEvaluation,
  useDeleteEvaluation,
  useTrainings,
  useCreateTraining,
  useDeleteTraining,
  useUpdateCompliance,
  useEmployeeRecords,
  useCreateRecord,
  useDeleteRecord,
  useResetEmployeePassword,
  type EmployeeRecord,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials, formatDateOnly } from '@isoft-people/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const TABS = [
  'Datos Generales',
  'Contacto',
  'Perfil del Puesto',
  'Desempeño',
  'Reconocimientos',
  'Cumplimiento',
  'Documentos',
] as const;

type Tab = (typeof TABS)[number];

const DOC_CATEGORIES = [
  { key: 'DPI', label: 'DPI' },
  { key: 'PASSPORT', label: 'Pasaporte' },
  { key: 'UNIVERSITY_DEGREE', label: 'Título Universitario' },
  { key: 'POLICE_RECORD', label: 'Antecedentes Policíacos' },
  { key: 'CRIMINAL_RECORD', label: 'Antecedentes Penales' },
  { key: 'RESIDENCE_PROOF', label: 'Constancia de Residencia' },
  { key: 'CV', label: 'Currículum Vitae' },
  { key: 'CONTRACT', label: 'Contrato' },
  { key: 'TAX_ID', label: 'NIT / Tax ID' },
  { key: 'OTHER', label: 'Otro' },
];

const STATUS_STYLES: Record<string, string> = {
  VALID: 'text-accent bg-accent-light',
  PENDING: 'text-warning bg-yellow-50',
  EXPIRED: 'text-error bg-red-50',
  REJECTED: 'text-error bg-red-50',
};

const DOC_STATUS_VARIANT: Record<
  string,
  'success' | 'warning' | 'error' | 'neutral'
> = {
  VALID: 'success',
  PENDING: 'warning',
  EXPIRED: 'error',
  REJECTED: 'error',
};

const DOC_STATUS_LABELS: Record<string, string> = {
  VALID: 'Válido',
  PENDING: 'Pendiente',
  EXPIRED: 'Vencido',
  REJECTED: 'Rechazado',
};

const RATING_LABELS: Record<string, string> = {
  OUTSTANDING: 'Sobresaliente',
  EXCEEDS: 'Excede',
  MEETS: 'Cumple',
  NEEDS_IMPROVEMENT: 'Necesita mejora',
  UNSATISFACTORY: 'Insatisfactorio',
};

const RATING_COLORS: Record<string, string> = {
  OUTSTANDING: 'bg-accent-light text-accent',
  EXCEEDS: 'bg-blue-50 text-info',
  MEETS: 'bg-primary-subtle text-primary',
  NEEDS_IMPROVEMENT: 'bg-yellow-50 text-warning',
  UNSATISFACTORY: 'bg-red-50 text-error',
};

export function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: e, isLoading } = useEmployee(id);
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === 'ADMIN' || role === 'HR';
  const [tab, setTab] = useState<Tab>('Datos Generales');
  const deactivate = useDeleteEmployee();

  const handleDeactivate = async () => {
    if (!e) return;
    if (
      !confirm(
        `¿Desactivar a ${e.firstName} ${e.lastName}?\n\nSu estado pasará a TERMINATED y no podrá iniciar sesión. Podrás reactivarlo después desde Editar.`,
      )
    )
      return;
    try {
      await deactivate.mutateAsync(e.id);
      navigate('/colaboradores');
    } catch (err: any) {
      alert(err?.response?.data?.error?.message ?? 'No se pudo desactivar');
    }
  };

  if (isLoading)
    return <p className="py-10 text-center text-font-secondary">Cargando...</p>;
  if (!e)
    return (
      <p className="py-10 text-center text-font-secondary">
        Colaborador no encontrado.
      </p>
    );

  const fullName = `${e.firstName} ${e.lastName}`;

  return (
    <PageTransition className="flex flex-col gap-6">
      <Link
        to="/colaboradores"
        className="inline-flex items-center gap-2 text-sm font-medium text-font-secondary transition-colors hover:text-primary"
      >
        <ArrowLeft size={16} /> Volver a colaboradores
      </Link>

      {/* Header card */}
      <FadeIn>
        <div className="flex flex-wrap items-center gap-5 rounded-xl border border-border bg-white p-6 shadow-sm">
          {e.photoUrl ? (
            <img
              src={e.photoUrl}
              alt={fullName}
              className="h-20 w-20 flex-shrink-0 rounded-full object-cover shadow-sm"
            />
          ) : (
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light text-2xl font-semibold text-white shadow-sm">
              {initials(fullName)}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-font-primary">{fullName}</h1>
            <p className="mt-1 text-sm text-font-secondary">
              {e.position?.title ?? '–'} · {e.country?.nameEs ?? '–'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {e.status === 'TERMINATED' ? (
                <Badge variant="error" dot>
                  Dado de baja
                </Badge>
              ) : (
                <Badge variant="success" dot>
                  Activo
                </Badge>
              )}
              <span className="text-xs text-font-tertiary">
                Desde {formatDateOnly(e.hireDate)} · #
                {e.employeeNumber}
              </span>
            </div>
          </div>

          {canEdit && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button asChild variant="secondary">
                <Link to={`/colaboradores/${e.id}/editar`}>
                  <Pencil size={14} /> Editar
                </Link>
              </Button>
              <ResetPasswordAction
                employeeId={e.id}
                fallbackEmail={e.corporateEmail ?? e.user?.email}
                viewerRole={role}
              />
              {e.status !== 'TERMINATED' && (
                <Button
                  variant="destructive"
                  onClick={handleDeactivate}
                  disabled={deactivate.isPending}
                >
                  <UserX size={14} />{' '}
                  {deactivate.isPending ? 'Desactivando...' : 'Desactivar'}
                </Button>
              )}
            </div>
          )}
        </div>
      </FadeIn>

      {/* Tabs */}
      <FadeIn delay={0.05}>
      <div className="rounded-xl border border-border bg-white shadow-sm">
        <div className="flex flex-wrap gap-1 border-b border-border px-6">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`relative px-4 py-4 text-sm font-semibold transition ${
                tab === t
                  ? 'text-primary'
                  : 'text-font-secondary hover:text-font-primary'
              }`}
            >
              {t}
              {tab === t && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === 'Datos Generales' && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
              <Field label="Nombre completo" value={fullName} />
              <Field label="Número de colaborador" value={e.employeeNumber} />
              <Field label="Puesto" value={e.position?.title} />
              <Field label="Departamento" value={e.department?.name} />
              <Field label="País" value={e.country?.nameEs} />
              <Field
                label="Fecha de ingreso"
                value={formatDateOnly(e.hireDate, {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              />
              <Field
                label="Jefe directo"
                value={
                  e.manager ? `${e.manager.firstName} ${e.manager.lastName}` : '–'
                }
              />
              <Field label="Modalidad" value={e.workMode ?? 'Presencial'} />
            </div>
          )}

          {tab === 'Contacto' && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
              <Field label="Correo corporativo" value={e.corporateEmail} />
              <Field label="Correo personal" value={e.personalEmail} />
              <Field label="Teléfono" value={e.phone} />
              <Field label="Dirección" value={e.address} />
              <Field label="Contacto de emergencia" value={e.emergencyContact} />
              <Field label="Teléfono de emergencia" value={e.emergencyPhone} />
            </div>
          )}

          {tab === 'Perfil del Puesto' && (
            <div className="space-y-4">
              <Field
                label="Función principal"
                value={e.mainFunction ?? 'Sin definir'}
              />
              {[e.responsibility1, e.responsibility2, e.responsibility3]
                .filter(Boolean)
                .map((r, i) => (
                  <p key={i} className="text-sm text-font-primary">
                    • {r}
                  </p>
                ))}
            </div>
          )}

          {tab === 'Desempeño' && <PerformanceTab employeeId={e.id} />}
          {tab === 'Reconocimientos' && (
            <RecordsTab employeeId={e.id} canManage={canEdit} />
          )}
          {tab === 'Cumplimiento' && <ComplianceTab employee={e} />}
          {tab === 'Documentos' && <DocumentsTab employeeId={e.id} />}
        </div>
      </div>
      </FadeIn>
    </PageTransition>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-font-tertiary">
        {label}
      </p>
      <p className="mt-1 text-sm text-font-primary">{value || '–'}</p>
    </div>
  );
}

// ==================== DOCUMENTS ====================
function DocumentsTab({ employeeId }: { employeeId: string }) {
  const { data, isLoading, refetch } = useEmployeeDocuments(employeeId);
  const initUpload = useInitDocumentUpload();
  const confirmUpload = useConfirmDocument();
  const updateDoc = useUpdateDocument();
  const del = useDeleteDocument();
  const download = useDocumentDownloadUrl();
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === 'ADMIN' || role === 'HR';
  const fileRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState('DPI');
  const [uploading, setUploading] = useState(false);

  const handleFilePicked = async (file: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const { document: newDoc, uploadUrl } = await initUpload.mutateAsync({
        employeeId,
        payload: {
          category,
          name: DOC_CATEGORIES.find((c) => c.key === category)?.label ?? file.name,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
        },
      });
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!putRes.ok) throw new Error('S3 upload failed');
      // Confirmar: marca el documento como VALID ahora que la subida terminó
      try {
        await confirmUpload.mutateAsync({ id: newDoc.id, status: 'VALID' });
      } catch (err) {
        console.warn('No se pudo confirmar el documento', err);
      }
      await refetch();
    } catch (err) {
      alert('No se pudo subir el documento');
      console.error(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Abrir la pestaña de forma SÍNCRONA (dentro del gesto del click) y luego
  // redirigirla cuando llega la URL presignada — evita el bloqueo de popups
  // que impedía visualizar/descargar documentos.
  const openDoc = async (id: string, inline: boolean) => {
    const w = window.open('', '_blank');
    try {
      const { url } = await download.mutateAsync({ id, inline });
      if (w) w.location.href = url;
      else window.location.href = url; // fallback si el popup fue bloqueado
    } catch (err) {
      if (w) w.close();
      alert('No se pudo abrir el documento. Intenta de nuevo.');
      console.error(err);
    }
  };

  const handleDownload = (id: string) => openDoc(id, false);
  const handleView = (id: string) => openDoc(id, true);

  const handleChangeStatus = async (id: string, status: string) => {
    await updateDoc.mutateAsync({ id, payload: { status } });
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-subtle">
            <FileText size={18} className="text-primary" />
          </span>
          <h3 className="text-base font-semibold text-font-primary">
            Documentos del Colaborador
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-10 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          >
            {DOC_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFilePicked(f);
            }}
          />
          <Button
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={14} /> {uploading ? 'Subiendo...' : 'Subir documento'}
          </Button>
        </div>
      </div>
      {isLoading && <p className="text-sm text-font-secondary">Cargando...</p>}
      {!isLoading && (data?.items?.length ?? 0) === 0 && (
        <p className="py-6 text-center text-sm text-font-tertiary">
          Aún no hay documentos cargados para este colaborador.
        </p>
      )}
      {(data?.items?.length ?? 0) > 0 && (
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
              <th className="py-3">Documento</th>
              <th className="py-3">Fecha</th>
              <th className="py-3">Tamaño</th>
              <th className="py-3">Estado</th>
              <th className="py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data!.items.map((d: any) => (
              <tr
                key={d.id}
                className="border-b border-border-light transition-colors hover:bg-primary-subtle/40"
              >
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-red-50 text-error">
                      <FileText size={16} />
                    </div>
                    <div>
                      <p className="font-semibold text-font-primary">{d.name}</p>
                      <p className="text-xs text-font-tertiary">
                        {d.fileName} ·{' '}
                        {DOC_CATEGORIES.find((c) => c.key === d.category)?.label ??
                          d.category}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 text-font-secondary">
                  {new Date(d.uploadedAt).toLocaleDateString('es')}
                </td>
                <td className="py-4 text-font-secondary">
                  {(d.size / 1024).toFixed(0)} KB
                </td>
                <td className="py-4">
                  {canManage ? (
                    <select
                      value={d.status}
                      onChange={(ev) => handleChangeStatus(d.id, ev.target.value)}
                      className={`rounded-md border border-border px-2 py-1 text-[11px] font-semibold ${
                        STATUS_STYLES[d.status] ?? 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <option value="VALID">Válido</option>
                      <option value="PENDING">Pendiente</option>
                      <option value="EXPIRED">Vencido</option>
                      <option value="REJECTED">Rechazado</option>
                    </select>
                  ) : (
                    <Badge variant={DOC_STATUS_VARIANT[d.status] ?? 'neutral'} dot>
                      {DOC_STATUS_LABELS[d.status] ?? d.status}
                    </Badge>
                  )}
                </td>
                <td className="py-4">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleView(d.id)}
                      title="Ver"
                      className="flex h-8 w-8 items-center justify-center rounded-md text-font-secondary hover:bg-bg"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={() => handleDownload(d.id)}
                      title="Descargar"
                      className="flex h-8 w-8 items-center justify-center rounded-md text-font-secondary hover:bg-bg"
                    >
                      <Download size={14} />
                    </button>
                    {canManage && (
                      <button
                        onClick={() => {
                          if (confirm('¿Eliminar este documento?')) del.mutate(d.id);
                        }}
                        title="Eliminar"
                        className="flex h-8 w-8 items-center justify-center rounded-md text-error hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ==================== PERFORMANCE ====================
function PerformanceTab({ employeeId }: { employeeId: string }) {
  const { data, isLoading } = useEvaluations(employeeId);
  const create = useCreateEvaluation();
  const del = useDeleteEvaluation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    evaluationDate: new Date().toISOString().slice(0, 10),
    rating: 'MEETS',
    score: '',
    periodLabel: '',
    strengths: '',
    improvementAreas: '',
    developmentPlan: '',
    notes: '',
  });

  const handleSave = async () => {
    await create.mutateAsync({
      employeeId,
      payload: {
        evaluationDate: form.evaluationDate,
        rating: form.rating,
        score: form.score ? Number(form.score) : undefined,
        periodLabel: form.periodLabel || undefined,
        strengths: form.strengths || undefined,
        improvementAreas: form.improvementAreas || undefined,
        developmentPlan: form.developmentPlan || undefined,
        notes: form.notes || undefined,
      },
    });
    setOpen(false);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-subtle">
            <Star size={18} className="text-primary" />
          </span>
          <h3 className="text-base font-semibold text-font-primary">
            Evaluaciones de Desempeño
          </h3>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus size={14} /> Nueva evaluación
        </Button>
      </div>

      {isLoading && <p className="text-sm text-font-secondary">Cargando...</p>}
      {!isLoading && (data?.items?.length ?? 0) === 0 && (
        <p className="py-6 text-center text-sm text-font-tertiary">
          Sin evaluaciones registradas.
        </p>
      )}

      <div className="space-y-3 stagger">
        {data?.items?.map((ev: any) => (
          <div
            key={ev.id}
            className="lift rounded-xl border border-border p-4 transition-colors hover:border-primary"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Star size={14} className="text-warning" />
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      RATING_COLORS[ev.rating] ?? ''
                    }`}
                  >
                    {RATING_LABELS[ev.rating] ?? ev.rating}
                  </span>
                  {ev.score != null && (
                    <span className="text-xs font-semibold text-font-secondary">
                      Puntaje: {ev.score}
                    </span>
                  )}
                  <span className="text-xs text-font-tertiary">
                    {new Date(ev.evaluationDate).toLocaleDateString('es')}
                    {ev.periodLabel ? ` · ${ev.periodLabel}` : ''}
                  </span>
                </div>
                {ev.strengths && (
                  <p className="mt-2 text-sm text-font-primary">
                    <strong>Fortalezas:</strong> {ev.strengths}
                  </p>
                )}
                {ev.improvementAreas && (
                  <p className="mt-1 text-sm text-font-primary">
                    <strong>Áreas de mejora:</strong> {ev.improvementAreas}
                  </p>
                )}
                {ev.developmentPlan && (
                  <p className="mt-1 text-sm text-font-primary">
                    <strong>Plan de desarrollo:</strong> {ev.developmentPlan}
                  </p>
                )}
                {ev.notes && (
                  <p className="mt-1 text-sm text-font-secondary">{ev.notes}</p>
                )}
              </div>
              <button
                onClick={() => {
                  if (confirm('¿Eliminar evaluación?')) del.mutate(ev.id);
                }}
                title="Eliminar"
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-error transition-colors hover:bg-red-50"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-primary-dark/40 p-4 backdrop-blur-sm">
          <div className="anim-scale-in max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold text-font-primary">
              Nueva evaluación
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-font-secondary">Fecha</label>
                <input
                  type="date"
                  value={form.evaluationDate}
                  onChange={(e) =>
                    setForm({ ...form, evaluationDate: e.target.value })
                  }
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Calificación</label>
                <select
                  value={form.rating}
                  onChange={(e) => setForm({ ...form, rating: e.target.value })}
                  className="input h-10 w-full"
                >
                  {Object.entries(RATING_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-font-secondary">Puntaje (0-100)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.score}
                  onChange={(e) => setForm({ ...form, score: e.target.value })}
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Período</label>
                <input
                  type="text"
                  value={form.periodLabel}
                  onChange={(e) => setForm({ ...form, periodLabel: e.target.value })}
                  className="input h-10 w-full"
                  placeholder="Q1 2026"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-font-secondary">Fortalezas</label>
                <textarea
                  value={form.strengths}
                  onChange={(e) => setForm({ ...form, strengths: e.target.value })}
                  className="input min-h-[60px] w-full"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-font-secondary">Áreas de mejora</label>
                <textarea
                  value={form.improvementAreas}
                  onChange={(e) =>
                    setForm({ ...form, improvementAreas: e.target.value })
                  }
                  className="input min-h-[60px] w-full"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-font-secondary">Plan de desarrollo</label>
                <textarea
                  value={form.developmentPlan}
                  onChange={(e) =>
                    setForm({ ...form, developmentPlan: e.target.value })
                  }
                  className="input min-h-[60px] w-full"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={create.isPending}>
                {create.isPending ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== COMPLIANCE ====================
function ComplianceTab({ employee }: { employee: any }) {
  const update = useUpdateCompliance();
  const { data: trainings } = useTrainings(employee.id);
  const createTr = useCreateTraining();
  const delTr = useDeleteTraining();
  const [flags, setFlags] = useState({
    codeOfConductSigned: employee.codeOfConductSigned ?? false,
    policiesSigned: employee.policiesSigned ?? false,
    trainingsCompleted: employee.trainingsCompleted ?? false,
    complianceNotes: employee.complianceNotes ?? '',
  });
  const [trForm, setTrForm] = useState({
    name: '',
    category: '',
    completedDate: '',
    hours: '',
  });

  const toggleFlag = async (key: keyof typeof flags) => {
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    await update.mutateAsync({ employeeId: employee.id, payload: next });
  };

  const saveNotes = async () => {
    await update.mutateAsync({ employeeId: employee.id, payload: flags });
  };

  const addTraining = async () => {
    if (!trForm.name) return;
    await createTr.mutateAsync({
      employeeId: employee.id,
      payload: {
        name: trForm.name,
        category: trForm.category || undefined,
        completedDate: trForm.completedDate || undefined,
        hours: trForm.hours ? Number(trForm.hours) : undefined,
      },
    });
    setTrForm({ name: '', category: '', completedDate: '', hours: '' });
  };

  const flagItems = [
    { key: 'codeOfConductSigned' as const, label: 'Código de conducta firmado' },
    { key: 'policiesSigned' as const, label: 'Políticas internas firmadas' },
    { key: 'trainingsCompleted' as const, label: 'Capacitaciones completadas' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-3 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-subtle">
            <CheckCircle2 size={18} className="text-primary" />
          </span>
          <h3 className="text-base font-semibold text-font-primary">
            Cumplimiento Normativo
          </h3>
        </div>
        <div className="space-y-2">
          {flagItems.map((it) => (
            <button
              key={it.key}
              onClick={() => toggleFlag(it.key)}
              className="flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-primary-subtle/30"
            >
              {flags[it.key] ? (
                <CheckCircle2 size={18} className="text-accent" />
              ) : (
                <Circle size={18} className="text-font-tertiary" />
              )}
              <span className="text-sm font-semibold text-font-primary">{it.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-4">
          <label className="text-xs text-font-secondary">Notas</label>
          <textarea
            value={flags.complianceNotes}
            onChange={(e) => setFlags({ ...flags, complianceNotes: e.target.value })}
            onBlur={saveNotes}
            className="input mt-1 min-h-[80px] w-full"
          />
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-subtle">
            <Star size={18} className="text-primary" />
          </span>
          <h3 className="text-base font-semibold text-font-primary">
            Capacitaciones
          </h3>
        </div>
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-12">
          <input
            placeholder="Nombre"
            value={trForm.name}
            onChange={(e) => setTrForm({ ...trForm, name: e.target.value })}
            className="input sm:col-span-4 h-10"
          />
          <input
            placeholder="Categoría"
            value={trForm.category}
            onChange={(e) => setTrForm({ ...trForm, category: e.target.value })}
            className="input sm:col-span-3 h-10"
          />
          <input
            type="date"
            value={trForm.completedDate}
            onChange={(e) => setTrForm({ ...trForm, completedDate: e.target.value })}
            className="input sm:col-span-2 h-10"
          />
          <input
            type="number"
            placeholder="Horas"
            value={trForm.hours}
            onChange={(e) => setTrForm({ ...trForm, hours: e.target.value })}
            className="input sm:col-span-2 h-10"
          />
          <Button
            onClick={addTraining}
            className="sm:col-span-1 w-full px-0"
            title="Agregar capacitación"
          >
            <Plus size={14} />
          </Button>
        </div>
        <div className="space-y-2 stagger">
          {trainings?.items?.map((t: any) => (
            <div
              key={t.id}
              className="lift flex items-center justify-between rounded-xl border border-border p-3 transition-colors hover:border-primary"
            >
              <div>
                <p className="text-sm font-semibold text-font-primary">{t.name}</p>
                <p className="text-xs text-font-tertiary">
                  {t.category ?? '–'} ·{' '}
                  {t.completedDate
                    ? new Date(t.completedDate).toLocaleDateString('es')
                    : 'Sin fecha'}{' '}
                  · {t.hours ?? 0} h
                </p>
              </div>
              <button
                onClick={() => {
                  if (confirm('¿Eliminar capacitación?')) delTr.mutate(t.id);
                }}
                title="Eliminar"
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-error transition-colors hover:bg-red-50"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {(trainings?.items?.length ?? 0) === 0 && (
            <p className="py-4 text-center text-sm text-font-tertiary">
              Sin capacitaciones registradas.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== RESET PASSWORD (acción en header) ====================
function ResetPasswordAction({
  employeeId,
  fallbackEmail,
  viewerRole,
}: {
  employeeId: string;
  fallbackEmail?: string;
  viewerRole?: string;
}) {
  const reset = useResetEmployeePassword();
  const [result, setResult] = useState<{
    email: string;
    password: string;
    created: boolean;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // Solo el ADMINISTRADOR puede reiniciar contraseñas.
  if (viewerRole !== 'ADMIN') return null;

  const handle = async () => {
    if (
      !confirm(
        `¿Reiniciar la contraseña de ${fallbackEmail ?? 'este colaborador'}?\n\nSi no tiene cuenta de acceso, se le creará una. Se generará una contraseña temporal que deberá cambiar en su primer ingreso.`,
      )
    )
      return;
    try {
      const r = await reset.mutateAsync(employeeId);
      setResult({ email: r.email, password: r.password, created: r.created });
    } catch (err: any) {
      alert(err?.response?.data?.error?.message ?? 'No se pudo reiniciar la contraseña');
    }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  return (
    <>
      <Button variant="outline" onClick={handle} disabled={reset.isPending}>
        <KeyRound size={14} /> {reset.isPending ? 'Reiniciando...' : 'Reiniciar contraseña'}
      </Button>

      {result && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-font-primary/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl anim-pop">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div className="flex items-center gap-2">
                <KeyRound size={18} className="text-primary" />
                <h3 className="text-base font-semibold text-font-primary">
                  Contraseña temporal
                </h3>
              </div>
              <button
                onClick={() => setResult(null)}
                className="text-font-tertiary hover:text-font-primary"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              {result.created && (
                <div className="rounded-md border border-accent/30 bg-accent-light/50 px-3 py-2 text-xs font-semibold text-accent">
                  Se creó la cuenta de acceso del colaborador con el correo{' '}
                  {result.email}.
                </div>
              )}
              <p className="text-sm text-font-secondary">
                Entrégala a <strong className="text-font-primary">{result.email}</strong> por
                un canal seguro. Solo se muestra una vez; deberá cambiarla al ingresar.
              </p>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 select-all rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm tracking-wider text-font-primary">
                  {result.password}
                </code>
                <button
                  onClick={copy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 text-xs font-semibold text-font-primary hover:bg-bg"
                >
                  {copied ? (
                    <>
                      <Check size={14} className="text-accent" /> Copiado
                    </>
                  ) : (
                    <>
                      <Copy size={14} /> Copiar
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="flex justify-end border-t border-border px-6 py-4">
              <Button onClick={() => setResult(null)}>Listo</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ==================== RECONOCIMIENTOS (premios / llamados) ====================
const REC_SEVERITY: Record<string, { label: string; variant: 'neutral' | 'warning' | 'error' }> = {
  MINOR: { label: 'Leve', variant: 'neutral' },
  MODERATE: { label: 'Moderada', variant: 'warning' },
  SEVERE: { label: 'Grave', variant: 'error' },
};

function RecordsTab({ employeeId, canManage }: { employeeId: string; canManage: boolean }) {
  const { data, isLoading } = useEmployeeRecords(employeeId);
  const create = useCreateRecord();
  const del = useDeleteRecord();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: 'AWARD' as 'AWARD' | 'WARNING',
    title: '',
    description: '',
    severity: 'MINOR',
    points: '',
    occurredOn: new Date().toISOString().slice(0, 10),
  });

  const items = data?.items ?? [];
  const awards = items.filter((r) => r.type === 'AWARD');
  const warnings = items.filter((r) => r.type === 'WARNING');

  const submit = async () => {
    if (!form.title.trim()) return;
    await create.mutateAsync({
      employeeId,
      payload: {
        type: form.type,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        severity: form.type === 'WARNING' ? form.severity : undefined,
        points: form.type === 'AWARD' && form.points ? Number(form.points) : undefined,
        occurredOn: form.occurredOn,
      },
    });
    setForm({
      type: 'AWARD',
      title: '',
      description: '',
      severity: 'MINOR',
      points: '',
      occurredOn: new Date().toISOString().slice(0, 10),
    });
    setShowForm(false);
  };

  const remove = async (rec: EmployeeRecord) => {
    if (!confirm(`¿Eliminar "${rec.title}"?`)) return;
    await del.mutateAsync({ recordId: rec.id, employeeId });
  };

  return (
    <div className="space-y-6">
      {canManage && (
        <div>
          {!showForm ? (
            <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
              <Plus size={14} /> Registrar premio o llamado
            </Button>
          ) : (
            <div className="rounded-xl border border-border bg-bg p-4">
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  onClick={() => setForm((f) => ({ ...f, type: 'AWARD' }))}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                    form.type === 'AWARD'
                      ? 'bg-accent text-white'
                      : 'border border-border bg-white text-font-secondary'
                  }`}
                >
                  <Trophy size={13} /> Premio
                </button>
                <button
                  onClick={() => setForm((f) => ({ ...f, type: 'WARNING' }))}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                    form.type === 'WARNING'
                      ? 'bg-warning text-white'
                      : 'border border-border bg-white text-font-secondary'
                  }`}
                >
                  <AlertTriangle size={13} /> Llamado de atención
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <input
                  className="input h-10 md:col-span-2"
                  placeholder="Título"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
                <textarea
                  className="input min-h-[60px] md:col-span-2"
                  placeholder="Descripción (opcional)"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
                <input
                  type="date"
                  className="input h-10"
                  value={form.occurredOn}
                  onChange={(e) => setForm((f) => ({ ...f, occurredOn: e.target.value }))}
                />
                {form.type === 'WARNING' ? (
                  <select
                    className="input h-10"
                    value={form.severity}
                    onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                  >
                    <option value="MINOR">Leve</option>
                    <option value="MODERATE">Moderada</option>
                    <option value="SEVERE">Grave</option>
                  </select>
                ) : (
                  <input
                    type="number"
                    className="input h-10"
                    placeholder="Puntos (opcional)"
                    value={form.points}
                    onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
                  />
                )}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={submit}
                  disabled={!form.title.trim() || create.isPending}
                >
                  {create.isPending ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-font-tertiary">Cargando...</p>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-font-tertiary">
          Sin premios ni llamados registrados.
        </p>
      ) : (
        <>
          {awards.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-accent">
                <Trophy size={13} /> Premios y reconocimientos
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {awards.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-xl border border-accent/30 bg-accent-light/40 p-4"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-font-primary">{r.title}</p>
                      {canManage && (
                        <button
                          onClick={() => remove(r)}
                          className="text-font-tertiary hover:text-error"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    {r.description && (
                      <p className="mt-0.5 text-xs text-font-secondary">{r.description}</p>
                    )}
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="text-[11px] text-font-tertiary">
                        {formatDateOnly(r.occurredOn)}
                      </span>
                      {r.points ? (
                        <Badge variant="success">
                          <Star size={10} /> {r.points} pts
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {warnings.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-warning">
                <AlertTriangle size={13} /> Llamados de atención
              </p>
              <div className="flex flex-col gap-2">
                {warnings.map((r) => {
                  const sev = r.severity ? REC_SEVERITY[r.severity] : null;
                  return (
                    <div
                      key={r.id}
                      className="flex items-start gap-3 rounded-lg border border-warning/30 bg-yellow-50 p-3"
                    >
                      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-warning" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-font-primary">{r.title}</p>
                          {sev && <Badge variant={sev.variant}>{sev.label}</Badge>}
                        </div>
                        {r.description && (
                          <p className="mt-0.5 text-xs text-font-secondary">{r.description}</p>
                        )}
                        <p className="mt-1 text-[11px] text-font-tertiary">
                          {formatDateOnly(r.occurredOn)}
                        </p>
                      </div>
                      {canManage && (
                        <button
                          onClick={() => remove(r)}
                          className="text-font-tertiary hover:text-error"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
